import * as Sentry from "@sentry/react-native";
import { useQuery } from "@tanstack/react-query";
import { observer } from "mobx-react";
import React, { useState } from "react";
import { Dropdown } from "~/components/V2/Dropdown";
import { useErrorAlertV2 } from "~/components/V2/error-alert-v2";
import type { BreedInstance } from "~/mobx/entities/breeds/Breed";
import type { PetInstance } from "~/mobx/entities/pets/Pet";
import { useStore } from "~/mobx/utils/useStore";
import { EditPetModal } from "./EditPetModal";

export const BreedPickerModal = observer(function BreedPickerModal({
  pet,
  isVisible,
  onClose,
  initialBreed,
}: {
  pet: PetInstance;
  isVisible: boolean;
  onClose: () => void;
  initialBreed?: BreedInstance | null;
}) {
  const store = useStore();
  const errorAlert = useErrorAlertV2();
  const [selectedBreed, setSelectedBreed] = useState<string | undefined>(
    initialBreed?.id ?? undefined,
  );

  const query = useQuery({
    queryKey: ["breeds"],
    queryFn: async () => {
      if (!pet.species?.id) {
        return [];
      }

      const breedList = await store.breedsStore.getBreedList(pet.species.id);

      return breedList;
    },
  });

  const breedList =
    query.data?.map((breed) => ({
      id: breed.id,
      title: breed.name,
    })) ?? [];

  return (
    <>
      {isVisible && (
        <EditPetModal
          onSave={async () => {
            try {
              await pet.updatePet({
                breedId: selectedBreed,
              });
            } catch (error) {
              console.log("Error while updating breed", error);
              errorAlert(error);
              Sentry.captureException(error);
            }

            onClose();
          }}
          onClose={onClose}
          title={intl.formatMessage({
            id: "s6azUm",
            defaultMessage: "Update pet breed",
            description: "Field for indicating the breed of a pet.",
          })}
        >
          <Dropdown
            items={breedList}
            label={intl.formatMessage({
              id: "C7GCQ3",
              defaultMessage: "Breed",
              description: "Label for the breed picker",
            })}
            onSelectItem={(item) => {
              setSelectedBreed(item);
            }}
            initialSelectedItemId={initialBreed?.id}
            selectedItemId={selectedBreed}
            floating={false}
          />
        </EditPetModal>
      )}
    </>
  );
});
